<template>
  <Row
    class="row main"
    :style="{ top: `${fixedAt}px` }"
  >
    <i-col class="filter">
      <Dropdown
        v-if="showViewSelector"
        trigger="click"
        placement="bottom-end"
        class="dropdown"
        @on-click="setView"
      >
        <div class="selected">
          <i :class="displayClass" />
          <i class="fas fa-angle-down" />
        </div>
        <DropdownMenu slot="list">
          <DropdownItem
            name="grid"
            class="options"
          >
            <i
              :class="isOptionActive(viewStyle === 'grid')"
              class="icon small"
            />
            <p>Grid</p>
            <i class="fas fa-th icon" />
          </DropdownItem>
          <DropdownItem
            name="list"
            class="options"
          >
            <i
              :class="isOptionActive(viewStyle === 'list')"
              class="icon small"
            />
            <p>List</p>
            <i class="fas fa-list-ul icon" />
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <Badge
        :dot="isFiltered"
        class="badge"
      >
        <Dropdown
          trigger="click"
          placement="bottom-end"
          class="dropdown"
        >
          <div
            class="selected"
            :class="{ 'active': isFiltered }"
          >
            <i class="fas fa-filter icon" />
            <i class="fas fa-angle-down" />
          </div>
          <DropdownMenu
            slot="list"
            class="elements"
          >
            <div class="row">
              <i
                v-if="showCheckMarks"
                :class="isOptionActive(filterParams.filterText)"
                class="icon small"
              />
              <div class="element-row">
                <p>By Keywords</p>
              </div>
            </div>
            <div class="row">
              <i
                v-if="showCheckMarks"
                class="icon small"
              />
              <Input
                v-model="filterParams.filterText"
                placeholder="Filter by Keyword"
                clearable
                class="select-input"
                @on-change="debouncedFilter"
              />
            </div>

            <slot name="top" />
            <div v-if="tagsList.length > 0">
              <div class="row">
                <i
                  v-if="showCheckMarks"
                  :class="isOptionActive(selectedTags.length > 0)"
                  class="icon small"
                />
                <div class="element-row">
                  <p>By Tags</p>
                </div>
              </div>
              <div class="row">
                <i
                  v-if="showCheckMarks"
                  class="icon small"
                />
                <Select
                  v-model="filterParams.selectedTags"
                  multiple
                  filterable
                  placeholder="Select Tag(s)"
                  :max-tag-count="2"
                  class="select-input capitalize big-list"
                  @on-change="updateSelectedTags"
                >
                  <Option
                    v-for="tag in tagsList"
                    :key="tag.name"
                    :label="tag.name"
                    :value="tag.name"
                  />
                </Select>
              </div>

              <div
                v-if="showTagControl"
                class="row"
              >
                <i
                  v-if="showCheckMarks"
                  :class="isOptionActive(selectedTags.length > 1)"
                  class="icon small"
                />
                <div class="buttons-row">
                  <p>Tags Condition</p>
                  <RadioGroup
                    v-model="filterParams.tagCondition"
                    type="button"
                    size="small"
                    @on-change="updateTagCondition"
                  >
                    <Radio
                      label="And"
                      class="radio-btn"
                    />
                    <Radio
                      label="Or"
                      class="radio-btn"
                    />
                  </RadioGroup>
                </div>
              </div>

              <div class="row">
                <i
                  v-if="showCheckMarks"
                  :class="isOptionActive(filterParams.selectedOwner)"
                  class="icon small"
                />
                <div class="element-row">
                  <p>By Owner</p>
                </div>
              </div>
              <div class="row">
                <i
                  v-if="showCheckMarks"
                  class="icon small"
                />
                <Select
                  v-model="filterParams.selectedOwner"
                  clearable
                  placeholder="Select Owner"
                  class="select-input big-list"
                  @on-change="updateOwner"
                >
                  <Option
                    v-for="owner in ownersList"
                    :key="owner.name"
                    :label="owner.name"
                    :value="owner.name"
                  />
                </Select>
              </div>
            </div>

            <slot name="bottom" />

            <div
              v-if="showFavoritesControl"
              class="row"
            >
              <i
                v-if="showCheckMarks"
                class="icon small"
              />
              <div
                class="element-row action"
                @click="onToggleFavorites"
              >
                <p>Only Favorites</p>
                <Checkbox
                  v-model="filterParams.isFilterByFavorites"
                  class="right"
                />
              </div>
            </div>

            <div class="row btn-section">
              <Button
                :disabled="!isFiltered"
                class="btn-reset"
                @click="resetFilters"
              >
                Reset Filters
                <i class="fas fa-trash icon" />
              </Button>
            </div>
          </DropdownMenu>
        </Dropdown>
      </Badge>
    </i-col>
  </Row>
</template>

<script>
import _ from 'lodash'

export default {
  model: {
    prop: 'filterText',
    event: 'change'
  },

  props: {
    viewType: {
      type: String,
      default: ''
    },
    showViewSelector: {
      type: Boolean,
      default: false
    },
    filterText: {
      type: String,
      default: ''
    },
    tagsList: {
      type: Array,
      default: () => []
    },
    selectedTags: {
      type: Array,
      default: () => []
    },
    showTagControl: {
      type: Boolean,
      default: false
    },
    tagCondition: {
      type: String,
      default: ''
    },
    ownersList: {
      type: Array,
      default: () => []
    },
    selectedOwner: {
      type: String,
      default: ''
    },
    showFavoritesControl: {
      type: Boolean,
      default: false
    },
    isFilterByFavorites: {
      type: Boolean,
      default: false
    },
    hasExternalFilters: {
      type: Boolean,
      default: false
    },
    showCheckMarks: {
      type: Boolean,
      default: false
    },
    fixedAt: {
      type: Number,
      default: 0
    }
  },

  data () {
    return {
      viewStyle: this.viewType || 'grid',
      filterParams: {
        filterText: this.filterText,
        selectedTags: this.selectedTags,
        tagsList: this.tagsList,
        tagCondition: this.tagCondition.toLowerCase(),
        selectedOwner: this.selectedOwner,
        isFilterByFavorites: this.isFilterByFavorites
      }
    }
  },

  computed: {
    displayClass () {
      return this.viewStyle === 'grid' ? 'fas fa-th icon' : 'fas fa-list-ul icon'
    },

    isFiltered () {
      return !_.isEmpty(this.filterParams.filterText) ||
          !_.isEmpty(this.filterParams.selectedTags) ||
          !_.isEmpty(this.filterParams.selectedOwner) ||
          this.filterParams.isFilterByFavorites ||
          this.hasExternalFilters
    }
  },

  watch: {
    tagsList (tagsList) {
      this.filterParams.tagsList = tagsList
    }
  },

  methods: {
    /**
       * Updates the results view by the selected view.
       * NOTE: It only applies for Dashboards.
       * @param {String} view
       */
    setView (view) {
      this.viewStyle = view
      this.$emit('onChangeView', this.viewStyle)
    },

    /**
       * Sets the checkmark class if the item is active.
       * @param {Boolean} isActive
       */
    isOptionActive (isActive) {
      return isActive ? 'fas fa-check' : ''
    },

    /**
       * Filters the results by keywords while the user types them into the filter textbox.
       * NOTE: It will update the filter after 500ms once the user typed last character on keypad.
       */
    debouncedFilter: _.debounce(function () {
      this.$emit('change', this.filterParams.filterText)
      this.$emit('onFilterResults', this.filterParams)
    }, 500),

    /**
       * Filters the results by the selected tags.
       * @param {Array} selectedTags
       */
    updateSelectedTags (selectedTags) {
      this.filterParams.tagsList.forEach(tag => (tag.selected = false))

      selectedTags.forEach(tagName => {
        const selectedTag = this.filterParams.tagsList.find(tag => tag.name === tagName)
        this.$set(selectedTag, 'selected', true)
      })

      this.$emit('onFilterResults', this.filterParams)
    },

    /**
       * Filters the results by the tag condition (AND / OR).
       * @param {String} tagCondition
       */
    updateTagCondition (tagCondition) {
      this.$set(this.filterParams, 'tagCondition', tagCondition.toLowerCase() || 'and')
      this.$emit('onFilterResults', this.filterParams)
    },

    /**
       * Filters the results by the selected owner.
       * @param {String} selectedOwner
       */
    updateOwner (selectedOwner) {
      this.$set(this.filterParams, 'selectedOwner', selectedOwner || '')
      this.$emit('onFilterResults', this.filterParams)
    },

    /**
       * Filters the results by favorites.
       */
    onToggleFavorites () {
      this.$set(this.filterParams, 'isFilterByFavorites', !this.filterParams.isFilterByFavorites)
      this.$emit('onFilterResults', this.filterParams)
    },

    /**
       * Removes any filter from the results.
       */
    resetFilters () {
      this.filterParams.tagsList.forEach(tag => (tag.selected = false))

      this.filterParams = {
        filterText: '',
        selectedTags: [],
        tagsList: this.filterParams.tagsList,
        tagCondition: 'and',
        selectedOwner: '',
        selectedType: '',
        isFilterByFavorites: false,
        resetFilters: true
      }
      this.$emit('onFilterResults', this.filterParams)
    }
  }
}
</script>

<style scoped>
  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
  }

  .row.main {
    position: sticky;
    z-index: 900;
    background-color: var(--color-body-background);
  }

  .icon {
    width: 16px;
    font-size: 16px;
    margin-right: 4px;
  }

  .icon.small {
    width: 12px;
    height: 12px;
    font-size: 12px;
    display: flex;
  }

  .capitalize {
    text-transform: capitalize;
  }

  .element-row.action {
    cursor: pointer;
  }

  .filter {
    margin: 0;
    padding: 16px 8px 8px;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .filter .badge {
    height: 16px;
  }

  .filter >>> .ivu-badge-dot {
    right: 8px;
  }

  .filter .dropdown {
    margin-left: 8px;
    width: 32px;
  }

  .filter .dropdown .selected {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    color: var(--color-black);
  }

  .filter .dropdown .options {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .filter .dropdown .options p {
    margin: 0 8px;
    width: 56px;
  }

  .filter .dropdown .options .icon.small {
    display: flex;
  }

  .filter .dropdown >>> .ivu-select-dropdown {
    width: auto;
  }

  .filter .dropdown .elements {
    padding: 0 8px;
    width: 240px;
  }

  .filter .dropdown .elements .row {
    margin: 0;
    padding: 4px 0;
  }

  .filter .dropdown .elements .row.btn-section {
    margin-top: 8px;
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .filter .dropdown .elements .row.btn-section .btn-reset {
    font-weight: bold;
    color: var(--color-white);
    border-color: var(--color-gray-border);
    background-color: var(--color-green);
  }

  .filter .dropdown .elements .row.btn-section .btn-reset:disabled {
    color: var(--color-mid-gray);
    border-color: var(--color-light-gray);
    background-color: var(--color-border);
  }

  .filter .dropdown .elements .row.btn-section .btn-reset .icon {
    margin-left: 8px;
  }

  .filter .dropdown .elements .row p,
  .filter .dropdown .elements .row .element-row,
  .filter .dropdown .elements .row .element-row p {
    margin: 0;
    padding: 0 8px;
    width: 100%;
  }

  .filter .dropdown .elements .row .element-row {
    padding: 0;
    display: flex;
    flex-direction: row;
  }

  .filter .dropdown .elements .row .element-row p {
    margin: 0;
  }

  .right {
    display: flex;
    justify-content: flex-end;
  }

  .filter .dropdown .elements .row .element-row .icon {
    margin: 0;
    width: 16px;
  }

  .filter .dropdown .elements .row .select-input {
    width: 100%;
  }

  .filter .dropdown .elements .row .select-input.left >>> .ivu-select-selection {
    width: 100%;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.right >>> .ivu-select-selection {
    width: 100%;
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.big-list >>> .ivu-select-dropdown {
    transform-origin: center top !important;
    top: unset !important;
    max-height: 400px;
    overflow-y: scroll;
  }

  .filter .dropdown .elements .row .select-input >>> .ivu-select-not-found {
    color: var(--color-red-alert);
    font-size: 12px;
    list-style: none;
    text-align: left;
    padding: 7px 16px;
  }

  .filter .dropdown .elements .dropdown-left {
    margin: 0;
    padding: 4px 0;
  }

  .filter .dropdown .elements .dropdown-left >>> .ivu-select-dropdown {
    margin-right: 8px;
  }

  .filter .dropdown .elements .row .buttons-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .filter .dropdown .elements .row .buttons-row p {
    width: calc(100% - 88px);
    margin-right: 8px;
  }

  .filter .radio-btn {
    padding: 0 8px;
    width: 40px;
    text-align: center;
    color: var(--color-black);
    border-color: var(--color-border);
  }

  .filter .radio-btn.ivu-radio-wrapper:after,
  .filter .radio-btn.ivu-radio-wrapper:before {
    content: none;
  }

  .filter .radio-btn.ivu-radio-wrapper-checked {
    background-color: var(--color-green-light);
    box-shadow: -1px 0 0 0 var(--color-border), 0 0 0 2px transparent;
    transition: none;
  }

  .search-box >>> .ivu-input.ivu-input-large {
    padding-right: 32px;
  }

  .search-box >>> .ivu-input-icon {
    right: 36px;
  }

  .search-box >>> .ivu-input-group-append {
    padding: 6px 8px;
    width: 36px;
  }

  .search-box >>> .ivu-input-group-append .ivu-btn {
    padding: 0;
    font-size: 16px;
    width: 16px;
    border: none;
  }
</style>
